import React from 'react';
import cs from 'classnames';
import * as yup from 'yup';
import Skeleton from 'react-loading-skeleton';
import { Box, Button, Flex, Text } from 'shared/components/display';

import DragAndDropUpload from 'shared/components/molecules/DragAndDropUpload/DragAndDropUpload';
import { ALL_BRKFST_SUPPORTED_MEDIA_FORMATS } from 'shared/config/fileFormats';
import { useFiles } from 'features/media/useFiles';
import { GeneralValidator, MultiValidator } from 'shared/utilities/validator';
import { useBriefAssetsNotification } from './useBriefAssetsNotification';
import { Input } from 'shared/lib/formik';
import { BrkfstFile } from 'shared/typings/file';

import styles from './styles';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import FieldErrorMessage from 'shared/components/FieldErrorMessage';
import { Lightbox } from 'shared/components/organisms/Lightbox';
import { CarouselItem } from 'shared/components/molecules/CarouselItem';
import LightboxFooter from 'shared/components/atoms/LightboxFooter/LightboxFooter';

interface Props {
  disabled?: boolean;
}

type Value = { file: BrkfstFile | null; uploading: boolean };

const BriefModuleContentInput: Input<Value, Props> = ({
  disabled,
  field: { name, value, onChange: formikOnChange },
}) => {
  const [lightbox, setLightbox] = React.useState(false);
  const { initiateSingleFileUpload } = useFiles();
  useBriefAssetsNotification({
    setEventHandlers: false,
  });

  const uploadFilesToModule = async (newFile) => {
    formikOnChange({ target: { name, value: { file: newFile.file, uploading: true } } });

    await initiateSingleFileUpload(
      {
        file: newFile.file,
        key: name,
        metadata: {
          isBrandAsset: true,
        },
      },
      new MultiValidator(new GeneralValidator(ALL_BRKFST_SUPPORTED_MEDIA_FORMATS)),
      (err) => {
        formikOnChange({ target: { name, value: { file: null, uploading: false } } });
      },
    );
  };

  const onRemove = () => {
    formikOnChange({ target: { name, value: { file: null, uploading: false } } });
  };

  const onRequestClose = () => {
    setLightbox(false);
  };

  if (value.uploading) {
    return (
      <Box>
        <Skeleton height={100} />
        <FieldErrorMessage name={name} />
      </Box>
    );
  }

  return (
    <Box css={styles} className={cs('brief-module-content-input')}>
      <Flex className="brief-module-content-input__wrapper">
        {value.file ? (
          <>
            <Flex className="brief-module-content-input__file">
              <img
                src={value?.file?.thumbnailUrl}
                alt={value?.file?.name}
                className="brief-module-content-input__thumbnail"
                onClick={() => setLightbox(true)}
              />
              <Text className="brief-module-content-input__file-name">{value?.file?.name}</Text>
            </Flex>
            <Button onClick={onRemove} variant={BUTTON_VARIANTS.TEXT} className="brief-module-content-input__remove">
              Remove
            </Button>
          </>
        ) : (
          <DragAndDropUpload
            onChange={uploadFilesToModule}
            acceptedMedia={ALL_BRKFST_SUPPORTED_MEDIA_FORMATS}
            disabled={disabled}
            text="Drag & Drop a GIF, Video, or Image"
          />
        )}
      </Flex>
      <Lightbox
        ariaLabel="Preview Example Content"
        isOpen={lightbox}
        selectedItemIndex={0}
        onRequestClose={onRequestClose}
      >
        {[
          <CarouselItem
            url={value?.file?.url}
            key="content-preview"
            thumbnailUrl={value?.file?.thumbnailUrl}
            mimeType={value?.file?.mimeType || ''}
            Footer={
              <LightboxFooter
                captionData={{
                  caption: value?.file?.name || '',
                }}
              />
            }
          />,
        ]}
      </Lightbox>
    </Box>
  );
};

export const briefModuleContentSchema = yup
  .object({
    file: yup.object().nullable(),
    uploading: yup.boolean().required(),
  })
  .test('not-uploading', 'File is still uploading', (value) => !value?.uploading);

export default BriefModuleContentInput;
